import React, { useState, useEffect } from 'react';
import {
  Shield, Plus, Edit2, Trash2, Key, CheckSquare, Square,
  ChevronDown, ChevronRight, Lock, AlertTriangle, X, Check,
  Search, ArrowLeft, Save,
} from 'lucide-react';
import { useRoles, usePermissions } from '../../../hooks/useRoles';
import * as rolesApi from '../../../api/roles';

const TIPO_COLOR = {
  ver:         'bg-blue-100 text-blue-700',
  crear:       'bg-green-100 text-green-700',
  editar:      'bg-yellow-100 text-yellow-700',
  eliminar:    'bg-red-100 text-red-700',
  aprobar:     'bg-purple-100 text-purple-700',
  procesar:    'bg-orange-100 text-orange-700',
  exportar:    'bg-teal-100 text-teal-700',
  configurar:  'bg-gray-100 text-gray-700',
  importar:    'bg-indigo-100 text-indigo-700',
};

// ── Página de Asignación de Permisos ─────────────────────────
function RolePermissionsPage({ role, onBack, onSaved }) {
  const { byModule, loading } = usePermissions();
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving]     = useState(false);
  const [search, setSearch]     = useState('');
  const [dirty, setDirty]       = useState(false);

  useEffect(() => {
    if (!role) return;
    rolesApi.getRoleById(role.id).then(data => {
      setSelected(new Set(data.permisos.map(p => p.id)));
      const exp = {};
      Object.keys(byModule).forEach(m => { exp[m] = true; });
      setExpanded(exp);
    });
  }, [role, byModule]);

  const toggle = (id) => {
    setDirty(true);
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleModule = (modulo, perms) => {
    setDirty(true);
    const ids = perms.map(p => p.id);
    const allSelected = ids.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) ids.forEach(id => next.delete(id));
      else             ids.forEach(id => next.add(id));
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await rolesApi.updateRolePerms(role.id, [...selected]);
      setDirty(false);
      onSaved();
      onBack();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al guardar permisos');
    } finally {
      setSaving(false);
    }
  };

  const filteredModules = Object.entries(byModule).reduce((acc, [mod, perms]) => {
    const filtered = search
      ? perms.filter(p =>
          p.nombre.toLowerCase().includes(search.toLowerCase()) ||
          p.codigo.toLowerCase().includes(search.toLowerCase())
        )
      : perms;
    if (filtered.length) acc[mod] = filtered;
    return acc;
  }, {});

  const totalPerms = Object.values(byModule).flat().length;

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft size={16} /> Volver a Roles
          </button>
          <span className="text-gray-300">|</span>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Key size={18} className="text-indigo-600" />
            </div>
            <div>
              <h1 className="text-base font-bold text-gray-900">
                Permisos — <span className="text-indigo-600">{role?.nombre}</span>
              </h1>
              <p className="text-xs text-gray-400">
                {selected.size} de {totalPerms} permisos seleccionados
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onBack}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={saving || !dirty}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40 transition-colors"
          >
            <Save size={15} />
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      </div>

      {/* Barra de progreso */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-500">Cobertura de permisos</span>
          <span className="text-xs font-bold text-indigo-600">{totalPerms > 0 ? Math.round(selected.size / totalPerms * 100) : 0}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2">
          <div
            className="bg-indigo-500 h-2 rounded-full transition-all duration-300"
            style={{ width: totalPerms > 0 ? `${selected.size / totalPerms * 100}%` : '0%' }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-400">
          {Object.entries(byModule).map(([mod, perms]) => {
            const sel = perms.filter(p => selected.has(p.id)).length;
            return (
              <span key={mod} title={mod} className="truncate max-w-[60px] text-center">
                <span className={sel === perms.length ? 'text-indigo-500 font-semibold' : sel > 0 ? 'text-amber-500' : ''}>
                  {sel}/{perms.length}
                </span>
              </span>
            );
          })}
        </div>
      </div>

      {/* Búsqueda */}
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
          placeholder="Buscar permiso o código..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Grid de módulos */}
      {loading ? (
        <div className="text-center py-16 text-gray-400">Cargando permisos...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(filteredModules).sort(([a], [b]) => a.localeCompare(b)).map(([modulo, perms]) => {
            const allSel  = perms.every(p => selected.has(p.id));
            const someSel = perms.some(p => selected.has(p.id));
            const isOpen  = expanded[modulo] !== false;
            const selCount = perms.filter(p => selected.has(p.id)).length;

            return (
              <div key={modulo} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                {/* Cabecera del módulo */}
                <button
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left transition-colors"
                  onClick={() => setExpanded(prev => ({ ...prev, [modulo]: !isOpen }))}
                >
                  <div className="flex items-center gap-3">
                    <div
                      onClick={e => { e.stopPropagation(); toggleModule(modulo, perms); }}
                      className="cursor-pointer shrink-0"
                    >
                      {allSel
                        ? <CheckSquare size={18} className="text-indigo-600" />
                        : someSel
                          ? <div className="w-[18px] h-[18px] border-2 border-indigo-400 rounded bg-indigo-100" />
                          : <Square size={18} className="text-gray-400" />
                      }
                    </div>
                    <span className="font-semibold text-gray-700 text-sm">{modulo}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                      selCount === perms.length ? 'bg-indigo-100 text-indigo-600' :
                      selCount > 0 ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                      {selCount}/{perms.length}
                    </span>
                  </div>
                  {isOpen
                    ? <ChevronDown size={15} className="text-gray-400 shrink-0" />
                    : <ChevronRight size={15} className="text-gray-400 shrink-0" />
                  }
                </button>

                {/* Permisos del módulo */}
                {isOpen && (
                  <div className="divide-y divide-gray-50">
                    {perms.map(p => (
                      <label key={p.id} className="flex items-start gap-3 px-4 py-2.5 hover:bg-indigo-50/40 cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          className="mt-0.5 accent-indigo-600 shrink-0"
                          checked={selected.has(p.id)}
                          onChange={() => toggle(p.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-gray-700">{p.nombre}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium shrink-0 ${TIPO_COLOR[p.tipo] || 'bg-gray-100 text-gray-600'}`}>
                              {p.tipo}
                            </span>
                          </div>
                          {p.descripcion && (
                            <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{p.descripcion}</p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Botón guardar al final (para scroll largo) */}
      {!loading && (
        <div className="flex justify-end pt-2 pb-6">
          <button
            onClick={save}
            disabled={saving || !dirty}
            className="flex items-center gap-2 px-6 py-2.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40 font-semibold transition-colors"
          >
            <Save size={15} />
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Modal de Formulario (crear/editar rol) ───────────────────
function RoleFormModal({ role, onClose, onSaved }) {
  const [form, setForm]     = useState({ nombre: role?.nombre || '', descripcion: role?.descripcion || '', estado: role?.estado || 'activo' });
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');
  const isEdit = !!role;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) { setErr('El nombre es requerido'); return; }
    setSaving(true);
    setErr('');
    try {
      if (isEdit) await rolesApi.updateRole(role.id, form);
      else        await rolesApi.createRole(form);
      onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">{isEdit ? 'Editar rol' : 'Nuevo rol'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        {isEdit && role?.es_sistema && (
          <div className="flex items-center gap-2 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
            <AlertTriangle size={16} />
            Los roles del sistema solo permiten editar la descripción.
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          {(!isEdit || !role?.es_sistema) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
              <input
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                value={form.nombre}
                onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                placeholder="Ej: Supervisor de Planta"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <textarea
              rows={3}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none"
              value={form.descripcion}
              onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
              placeholder="Descripción del rol y sus responsabilidades..."
            />
          </div>
          {(!isEdit || !role?.es_sistema) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                value={form.estado}
                onChange={e => setForm(f => ({ ...f, estado: e.target.value }))}
              >
                <option value="activo">Activo</option>
                <option value="inactivo">Inactivo</option>
              </select>
            </div>
          )}

          {err && <p className="text-sm text-red-600">{err}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear rol'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Módulo Principal ─────────────────────────────────────────
export default function RolesModule() {
  const { roles, loading, error, reload } = useRoles();
  const [permRole, setPermRole]     = useState(null);
  const [formModal, setFormModal]   = useState(null);
  const [deleting, setDeleting]     = useState(null);
  const [customizing, setCustomizing] = useState(null);
  const [search, setSearch]         = useState('');

  const handleDelete = async (role) => {
    const isOverride = role.company_id !== null;
    const msg = isOverride
      ? `¿Restablecer el rol "${role.nombre}" a la configuración global? Se perderán los permisos personalizados.`
      : `¿Eliminar el rol "${role.nombre}"? Esta acción no se puede deshacer.`;
    if (!window.confirm(msg)) return;
    setDeleting(role.id);
    try {
      await rolesApi.deleteRole(role.id);
      reload();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al eliminar');
    } finally {
      setDeleting(null);
    }
  };

  const handleCustomize = async (role) => {
    setCustomizing(role.id);
    try {
      const res = await rolesApi.customizeRole(role.id);
      reload();
      // Abrir el editor de permisos con el nuevo rol clonado
      setPermRole(res.role);
    } catch (e) {
      const msg = e.response?.data?.error || 'Error al personalizar';
      // Si ya existe, lo buscamos en la lista y abrimos directo
      if (e.response?.status === 409 && e.response.data?.existingId) {
        const existing = roles.find(r => r.id === e.response.data.existingId);
        if (existing) setPermRole(existing);
      } else {
        alert(msg);
      }
    } finally {
      setCustomizing(null);
    }
  };

  if (permRole) {
    return (
      <RolePermissionsPage
        role={permRole}
        onBack={() => { setPermRole(null); reload(); }}
        onSaved={reload}
      />
    );
  }

  const filtered = roles.filter(r =>
    !search ||
    r.nombre.toLowerCase().includes(search.toLowerCase()) ||
    r.codigo.toLowerCase().includes(search.toLowerCase())
  );

  // Roles propios de la empresa (personalizaciones + roles nuevos creados por la empresa)
  const companyRoles = filtered.filter(r => r.company_id !== null);
  // Roles globales del sistema
  const systemRoles  = filtered.filter(r => r.company_id === null);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Shield size={22} className="text-indigo-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Roles</h1>
            <p className="text-sm text-gray-500">Gestiona los roles y sus permisos en el sistema</p>
          </div>
        </div>
        <button
          onClick={() => setFormModal('new')}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
        >
          <Plus size={16} /> Nuevo rol
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Roles del sistema',   value: systemRoles.length,  color: 'text-amber-600'  },
          { label: 'De esta empresa',     value: companyRoles.length, color: 'text-indigo-600' },
          { label: 'Personalizados',      value: systemRoles.filter(r => r.customized_by_company).length, color: 'text-green-600' },
        ].map(s => (
          <div key={s.label} className="bg-white border rounded-xl p-4 text-center">
            <div className={`text-3xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-sm text-gray-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
          placeholder="Buscar rol..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {error   && <div className="p-4 bg-red-50 text-red-600 rounded-lg mb-4">{error}</div>}
      {loading && <div className="text-center py-12 text-gray-400">Cargando roles...</div>}

      {/* Roles de esta empresa */}
      {!loading && (
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
            <Shield size={14} /> Roles de esta empresa
          </h2>
          {companyRoles.length === 0 ? (
            <div className="text-center py-8 text-gray-400 border-2 border-dashed rounded-xl text-sm">
              Ningún rol personalizado aún. Personaliza uno del sistema o crea uno nuevo.
            </div>
          ) : (
            <div className="space-y-2">
              {companyRoles.map(role => (
                <RoleRow
                  key={role.id}
                  role={role}
                  isCompanyRole
                  onEdit={() => setFormModal(role)}
                  onPerms={() => setPermRole(role)}
                  onDelete={handleDelete}
                  deleting={deleting}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Plantillas del sistema */}
      {!loading && systemRoles.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
              <Lock size={14} /> Plantillas del sistema
            </h2>
            <span className="text-xs text-gray-400 font-normal normal-case">
              — Personaliza un rol del sistema para modificar sus permisos en esta empresa
            </span>
          </div>
          <div className="space-y-2">
            {systemRoles.map(role => (
              <RoleRow
                key={role.id}
                role={role}
                isCompanyRole={false}
                onEdit={() => setFormModal(role)}
                onPerms={() => handleCustomize(role)}
                onDelete={handleDelete}
                deleting={deleting}
                customizing={customizing === role.id}
                isCustomized={role.customized_by_company}
              />
            ))}
          </div>
        </section>
      )}

      {/* Modal crear/editar rol */}
      {formModal && (
        <RoleFormModal
          role={formModal === 'new' ? null : formModal}
          onClose={() => setFormModal(null)}
          onSaved={reload}
        />
      )}
    </div>
  );
}

function RoleRow({ role, isCompanyRole, onEdit, onPerms, onDelete, deleting, customizing, isCustomized }) {
  return (
    <div className={`flex items-center gap-4 bg-white border rounded-xl px-5 py-4 hover:shadow-sm transition-shadow ${
      !isCompanyRole ? 'opacity-90' : ''
    }`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-gray-800">{role.nombre}</span>
          {!isCompanyRole && (
            <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium flex items-center gap-1">
              <Lock size={10} /> Plantilla global
            </span>
          )}
          {isCustomized && (
            <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">
              ✓ Personalizado
            </span>
          )}
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
            role.estado === 'activo' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {role.estado}
          </span>
        </div>
        {role.descripcion && <p className="text-sm text-gray-500 mt-0.5 truncate">{role.descripcion}</p>}
        <p className="text-xs text-gray-400 mt-1 font-mono">{role.codigo}</p>
      </div>

      <div className="flex items-center gap-1 text-sm text-gray-500 min-w-[90px]">
        <Key size={14} className="text-indigo-400" />
        {role.total_permisos} permisos
      </div>

      <div className="flex items-center gap-1">
        {isCompanyRole ? (
          <button
            onClick={onPerms}
            title="Editar permisos"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-indigo-600 border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors font-medium"
          >
            <CheckSquare size={14} /> Permisos
          </button>
        ) : (
          <button
            onClick={onPerms}
            disabled={customizing || isCustomized}
            title={isCustomized ? 'Ya personalizado — edita en "Roles de esta empresa"' : 'Crear personalización para esta empresa'}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border rounded-lg transition-colors font-medium ${
              isCustomized
                ? 'text-gray-400 border-gray-200 bg-gray-50 cursor-not-allowed'
                : 'text-emerald-700 border-emerald-200 bg-emerald-50 hover:bg-emerald-100'
            }`}
          >
            <Plus size={14} />
            {customizing ? 'Creando...' : isCustomized ? 'Ya personalizado' : 'Personalizar'}
          </button>
        )}

        {isCompanyRole && (
          <button
            onClick={onEdit}
            title="Editar rol"
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Edit2 size={17} />
          </button>
        )}

        {isCompanyRole && (
          <button
            onClick={() => onDelete(role)}
            title={role.es_sistema ? 'Restablecer a global' : 'Eliminar rol'}
            disabled={deleting === role.id}
            className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
          >
            <Trash2 size={17} />
          </button>
        )}
      </div>
    </div>
  );
}
